export type SeverityTone = "extreme" | "high" | "moderate" | "low" | "minimal";

export type SeverityClasses = {
  tone: SeverityTone;
  text: string;
  bg: string;
  border: string;
  bar: string;
  badge: string;
};

const TONE_CLASSES: Record<SeverityTone, Omit<SeverityClasses, "tone">> = {
  extreme: {
    text: "text-red-800",
    bg: "bg-red-50",
    border: "border-red-200",
    bar: "bg-red-500",
    badge: "bg-red-600 text-white",
  },
  high: {
    text: "text-[#B45309]",
    bg: "bg-[#FFF7ED]",
    border: "border-[#FDBA74]",
    bar: "bg-orange-500",
    badge: "bg-orange-600 text-white",
  },
  moderate: {
    text: "text-[#92400E]",
    bg: "bg-[#FFFBEB]",
    border: "border-[#FCD34D]",
    bar: "bg-amber-500",
    badge: "bg-amber-500 text-white",
  },
  low: {
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    bar: "bg-emerald-500",
    badge: "bg-emerald-600 text-white",
  },
  minimal: {
    text: "text-teal-700",
    bg: "bg-teal-50",
    border: "border-teal-200",
    bar: "bg-teal-500",
    badge: "bg-teal-600 text-white",
  },
};

/** Normalize risk / complexity level strings (and numeric-like values) to a tone. */
export function getSeverityTone(level: string | number): SeverityTone {
  if (typeof level === "number" || /^\d+(\.\d+)?$/.test(String(level).trim())) {
    const n = typeof level === "number" ? level : Number(level);
    if (n >= 75) return "extreme";
    if (n >= 50) return "high";
    if (n >= 25) return "moderate";
    return "low";
  }

  const normalized = String(level).toLowerCase();
  if (normalized.includes("extreme") || normalized.includes("very high")) {
    return "extreme";
  }
  if (normalized.includes("minimal") || normalized.includes("lowest")) {
    return "minimal";
  }
  if (normalized.includes("high")) return "high";
  if (normalized.includes("moderate")) return "moderate";
  if (normalized.includes("low") || normalized.includes("lower")) return "low";
  return "low";
}

export function getSeverityClasses(level: string | number): SeverityClasses {
  const tone = getSeverityTone(level);
  return { tone, ...TONE_CLASSES[tone] };
}

/** Risk pill colors for predicted readmission risk. */
export function getRiskBadgeClass(risk: string): string {
  const normalized = risk.toLowerCase();
  if (normalized.includes("extreme")) return "bg-red-600 text-white";
  if (normalized.includes("very high")) return "bg-orange-600 text-white";
  if (normalized.includes("high")) return "bg-orange-500 text-white";
  if (normalized.includes("moderate")) return "bg-amber-500 text-white";
  return "bg-emerald-600 text-white";
}

export function isPrincipal(classification: string): boolean {
  return classification.toUpperCase().includes("PRINCIPAL");
}

export function hasCc(classification: string): boolean {
  const normalized = classification.toUpperCase().trim();
  if (normalized.includes("NON-CC") || normalized.includes("NONCC")) {
    return false;
  }
  return (
    normalized === "CC" ||
    /(^|[^A-Z])CC([^A-Z]|$)/.test(normalized)
  );
}

export function hasMcc(classification: string): boolean {
  return classification.toUpperCase().includes("MCC");
}

/** Extract NN% from contribution text; clamp 0–100. */
export function parseContributionPercent(contribution: string): number | null {
  const match = /(\d+(?:\.\d+)?)\s*%/.exec(contribution);
  if (!match) return null;
  const percent = Number(match[1]);
  if (!Number.isFinite(percent)) return null;
  return Math.min(100, Math.max(0, percent));
}

/** @deprecated Use parseContributionPercent */
export const parsePercentContribution = parseContributionPercent;

export type ComorbidityRiskTone = {
  accent: string;
  headerBg: string;
  border: string;
  iconColor: string;
  levelText: string;
};

/** Overall card tone from complexityScore (bars + percent text). */
export function comorbidityRiskTone(level: string | number): ComorbidityRiskTone {
  const tone = getSeverityTone(level);
  switch (tone) {
    case "extreme":
      return {
        accent: "bg-red-500",
        headerBg: "bg-red-50/70",
        border: "border-red-200",
        iconColor: "text-red-500",
        levelText: "text-red-700",
      };
    case "high":
      return {
        accent: "bg-orange-400",
        headerBg: "bg-orange-50/70",
        border: "border-orange-200",
        iconColor: "text-orange-500",
        levelText: "text-orange-600",
      };
    case "moderate":
      return {
        accent: "bg-amber-400",
        headerBg: "bg-amber-50/70",
        border: "border-amber-200",
        iconColor: "text-amber-500",
        levelText: "text-amber-700",
      };
    default:
      return {
        accent: "bg-emerald-400",
        headerBg: "bg-emerald-50/70",
        border: "border-emerald-200",
        iconColor: "text-emerald-500",
        levelText: "text-emerald-700",
      };
  }
}

/**
 * Badge colors for non-percent contribution labels
 * (HIGH / MODERATE / numeric thresholds / free text).
 */
export function complexityBadgeClass(level: string): string {
  const normalized = level.trim().toLowerCase();
  const numericLevel = Number(normalized);

  if (Number.isFinite(numericLevel) && normalized !== "") {
    if (numericLevel >= 75) return "bg-red-600 text-white";
    if (numericLevel >= 50) return "bg-orange-600 text-white";
    if (numericLevel >= 25) return "bg-amber-500 text-white";
    return "bg-emerald-600 text-white";
  }

  if (normalized.includes("extreme") || normalized.includes("very high")) {
    return "bg-red-600 text-white";
  }
  if (normalized.includes("high")) {
    return "bg-orange-600 text-white";
  }
  if (normalized.includes("moderate")) {
    return "bg-amber-500 text-white";
  }
  if (normalized.includes("low")) {
    return "bg-emerald-600 text-white";
  }
  return "bg-slate-700 text-white";
}
